import { Icon } from "@iconify/react/dist/iconify.js";
import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { setTokens } from "../store/reducers/auth/auth.reducer";
import { registroPublicoService } from "../services/registro/registro.service";
import Alert from "./UI/Alert";

const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY;

// Extrae el primer mensaje utilizable de una respuesta de campo del backend.
// Soporta strings, listas de strings y objetos anidados.
const primerMsg = (v) => {
  if (v == null) return null;
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return v.length ? primerMsg(v[0]) : null;
  if (typeof v === "object") {
    const first = Object.values(v)[0];
    return first ? primerMsg(first) : null;
  }
  return String(v);
};

const SignUpLayer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "",
    nombres: "",
    apellido_paterno: "",
    apellido_materno: "",
    password: "",
    password2: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  const resetCaptcha = () => {
    setCaptchaToken(null);
    captchaRef.current?.resetCaptcha();
  };

  const passwordsMatch =
    form.password && form.password2 && form.password === form.password2;

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const { email, nombres, apellido_paterno, password, password2 } = form;
    const errs = {};

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) errs.email = "El correo es obligatorio.";
    else if (!emailRegex.test(email.trim())) errs.email = "Email no válido.";

    if (!nombres.trim()) errs.nombres = "El nombre es obligatorio.";
    if (!apellido_paterno.trim()) errs.apellido_paterno = "El apellido paterno es obligatorio.";
    if (!password) errs.password = "La contraseña es obligatoria.";
    else if (password.length < 6) errs.password = "Mínimo 6 caracteres.";
    if (!password2) errs.password2 = "Confirma tu contraseña.";
    else if (password !== password2) errs.password2 = "Las contraseñas no coinciden.";

    if (!captchaToken) errs.captcha_token = "Completa el captcha para continuar.";

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }

    setLoading(true);
    try {
      const data = await registroPublicoService({
        email: email.trim().toLowerCase(),
        nombres: nombres.trim(),
        apellido_paterno: apellido_paterno.trim(),
        apellido_materno: form.apellido_materno.trim(),
        password,
        password2,
        captcha_token: captchaToken,
      });

      if (data.tokens?.access) {
        dispatch(setTokens({ access: data.tokens.access, user: data.user }));
        navigate("/", { replace: true });
      } else {
        navigate("/sign-in", {
          replace: true,
          state: {
            registrationSuccess: data.message || "¡Registro exitoso!",
          },
        });
      }
    } catch (err) {
      // Sin respuesta del servidor → problema de red
      if (!err.response) {
        setError(
          "No pudimos conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.",
        );
        return;
      }

      const server = err.response.data || {};
      // El backend usa un exception_handler que envuelve:
      //   { message: "...", errors: { <campo>: ["..."] }, status_code: 400 }
      // Aceptamos ambas formas: campos en `errors.<campo>` o directamente en la raíz.
      const fields = server.errors || server || {};
      const fieldErrs = {};

      const map = {
        email: "email",
        nombres: "nombres",
        apellido_paterno: "apellido_paterno",
        apellido_materno: "apellido_materno",
        password: "password",
        password2: "password2",
        captcha_token: "captcha_token",
      };
      for (const [key, target] of Object.entries(map)) {
        const msg = primerMsg(fields[key]);
        if (msg) fieldErrs[target] = msg;
      }

      const nonField = primerMsg(fields.non_field_errors);
      if (nonField) fieldErrs.non_field_errors = nonField;

      // El captcha es de un solo uso: siempre lo reiniciamos en error.
      resetCaptcha();

      if (Object.keys(fieldErrs).length > 0) {
        setFieldErrors(fieldErrs);
        return;
      }

      // Sin errores de campo — muestra el mensaje general del backend
      const general =
        server.message ||
        primerMsg(server.detail) ||
        "No se pudo completar el registro. Inténtalo de nuevo.";
      setError(general);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (field) =>
    `form-control h-48-px bg-neutral-50 radius-12 ${fieldErrors[field] ? "is-invalid" : ""}`;

  return (
    <section className="auth bg-base d-flex flex-wrap">
      <div className="auth-left d-lg-block d-none">
        <div className="d-flex align-items-center flex-column h-100 justify-content-center">
          <img src="assets/images/auth/auth-img.png" alt="" />
        </div>
      </div>
      <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
        <div className="max-w-464-px mx-auto w-100">
          <div>
            <Link to="/" className="mb-40 max-w-290-px">
              <img src="assets/images/logo.png" alt="" />
            </Link>
            <h4 className="mb-12">Crea tu cuenta</h4>
            <p className="mb-32 text-secondary-light text-lg">
              Regístrate para descubrir tus talentos. ¡Es gratis!
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {error && <Alert message={error} variant="error" onClose={() => setError(null)} size="sm" />}
            {fieldErrors.non_field_errors && (
              <Alert message={fieldErrors.non_field_errors} variant="error" onClose={() => setFieldErrors((p) => ({ ...p, non_field_errors: null }))} size="sm" />
            )}

              {/* Email */}
              <div className="icon-field mb-12">
                <span className="icon top-50 translate-middle-y"><Icon icon="mdi:email-outline" /></span>
                <input type="email" className={inputClass("email")} placeholder="tu@correo.edu.mx"
                  value={form.email} onChange={handleChange("email")} disabled={loading} autoComplete="email" />
              </div>
              {fieldErrors.email && <small className="text-danger d-block mb-12">{fieldErrors.email}</small>}

              {/* Nombres + Apellido paterno */}
              <div className="row g-2 mb-0">
                <div className="col-md-6">
                  <input type="text" className={inputClass("nombres")} placeholder="Nombre(s)"
                    value={form.nombres} onChange={handleChange("nombres")} disabled={loading} autoComplete="given-name" />
                  {fieldErrors.nombres && <small className="text-danger d-block mt-1">{fieldErrors.nombres}</small>}
                </div>
                <div className="col-md-6">
                  <input type="text" className={inputClass("apellido_paterno")} placeholder="Apellido paterno"
                    value={form.apellido_paterno} onChange={handleChange("apellido_paterno")} disabled={loading} autoComplete="family-name" />
                  {fieldErrors.apellido_paterno && <small className="text-danger d-block mt-1">{fieldErrors.apellido_paterno}</small>}
                </div>
              </div>

              {/* Apellido materno */}
              <input type="text" className={`${inputClass("apellido_materno")} mt-12`} placeholder="Apellido materno (opcional)"
                value={form.apellido_materno} onChange={handleChange("apellido_materno")} disabled={loading} autoComplete="additional-name" />

              {/* Password */}
              <div className="position-relative mt-12">
                <div className="icon-field">
                  <span className="icon top-50 translate-middle-y"><Icon icon="solar:lock-password-outline" /></span>
                  <input type={showPassword ? "text" : "password"} className={inputClass("password")} placeholder="Contraseña (mín. 6 caracteres)"
                    value={form.password} onChange={handleChange("password")} disabled={loading}
                    onPaste={(e) => e.preventDefault()} autoComplete="new-password" />
                </div>
                <span
                  className="cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  <Icon icon={showPassword ? "mdi:eye-off-outline" : "mdi:eye-outline"} width="22" />
                </span>
              </div>
              {fieldErrors.password && <small className="text-danger d-block mt-1">{fieldErrors.password}</small>}

              {/* Confirmar password */}
              <div className="position-relative mt-12">
                <div className="icon-field">
                  <span className="icon top-50 translate-middle-y"><Icon icon="solar:lock-password-outline" /></span>
                  <input type={showPassword2 ? "text" : "password"}
                    className={`form-control h-48-px bg-neutral-50 radius-12 ${fieldErrors.password2 ? "is-invalid" : passwordsMatch ? "border-success bg-success-subtle" : ""}`}
                    placeholder="Confirmar contraseña"
                    value={form.password2} onChange={handleChange("password2")} disabled={loading}
                    onPaste={(e) => e.preventDefault()} autoComplete="new-password" />
                </div>
                <span
                  className="cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light"
                  onClick={() => setShowPassword2((prev) => !prev)}
                >
                  <Icon icon={showPassword2 ? "mdi:eye-off-outline" : "mdi:eye-outline"} width="22" />
                </span>
              </div>
              {fieldErrors.password2 && <small className="text-danger d-block mt-1">{fieldErrors.password2}</small>}
              {passwordsMatch && !fieldErrors.password2 && (
                <small className="text-success d-block mt-1">
                  <Icon icon="mdi:check-circle" width="14" height="14" className="me-1" />
                  Las contraseñas coinciden
                </small>
              )}

              {HCAPTCHA_SITE_KEY && (
                <div className="mt-20 d-flex flex-column align-items-center">
                  <HCaptcha
                    ref={captchaRef}
                    sitekey={HCAPTCHA_SITE_KEY}
                    onVerify={(token) => {
                      setCaptchaToken(token);
                      if (fieldErrors.captcha_token) {
                        setFieldErrors((p) => ({ ...p, captcha_token: null }));
                      }
                    }}
                    onExpire={() => setCaptchaToken(null)}
                    onError={() => setCaptchaToken(null)}
                  />
                  {fieldErrors.captcha_token && (
                    <small className="text-danger d-block mt-2">{fieldErrors.captcha_token}</small>
                  )}
                </div>
              )}

              <button type="submit" disabled={loading}
                className="btn btn-primary text-sm btn-sm px-12 py-14 w-100 radius-12 d-flex align-items-center justify-content-center gap-2 mt-20"
                style={loading ? { opacity: 0.85, cursor: "not-allowed" } : undefined}>
                {loading ? (
                  <>
                    <span
                      aria-hidden="true"
                      style={{
                        width: 16,
                        height: 16,
                        border: "2px solid rgba(255,255,255,0.35)",
                        borderTopColor: "#ffffff",
                        borderRadius: "50%",
                        display: "inline-block",
                        animation: "signup-spin 0.8s linear infinite",
                      }}
                    />
                    Registrando…
                  </>
                ) : (
                  <>
                    <Icon icon="mdi:account-plus-outline" width="20" />
                    Crear cuenta
                  </>
                )}
              </button>
              <style>{`@keyframes signup-spin { to { transform: rotate(360deg); } }`}</style>

              <div className="mt-32 text-center text-sm">
                <p className="mb-0">
                  ¿Ya tienes cuenta?{" "}
                  <Link to="/sign-in" className="text-primary-600 fw-semibold">Iniciar sesión</Link>
                </p>
              </div>
            </form>
        </div>
      </div>
    </section>
  );
};

export default SignUpLayer;
